<template>
	<div class="content-wrapper">
		<div class="container flex-grow-1 container-p-y">

			<div class="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-6 row-gap-4">
				<div class="d-flex flex-column justify-content-center">
					<h4 class="mb-1">{{ $t('virtualClassrooms.list.title') }}</h4>
					<p class="mb-0">{{ $t('virtualClassrooms.list.description') }}</p>
				</div>
				<div class="d-flex align-content-center flex-wrap gap-3">
					<router-link to="/virtual-classrooms/new">
						<button type="button" class="btn btn-primary">
							<i class="ri-add-line me-1"></i>{{ $t('virtualClassrooms.list.createButton') }}
						</button>
					</router-link>
				</div>
			</div>

			<!-- Classrooms table -->
			<div class="card">
				<div class="table-responsive" v-if="classrooms.length > 0">
					<table class="table table-borderless">
						<thead>
							<tr>
								<th>{{ $t('virtualClassrooms.list.table.session') }}</th>
								<th>{{ $t('virtualClassrooms.list.table.dates') }}</th>
								<th>{{ $t('virtualClassrooms.list.table.days') }}</th>
								<th>{{ $t('virtualClassrooms.list.table.chapters') }}</th>
								<th>{{ $t('virtualClassrooms.list.table.participants') }}</th>
								<th>{{ $t('virtualClassrooms.list.table.platform') }}</th>
								<th class="text-end">{{ $t('virtualClassrooms.list.table.actions') }}</th>
							</tr>
						</thead>
						<tbody>
							<tr v-for="classroom in classrooms" :key="classroom._id" class="cursor-pointer" @click="openDetail(classroom._id)">
								<td>
									<div class="d-flex align-items-center">
										<div class="avatar avatar-sm me-3">
											<span class="avatar-initial rounded-circle bg-label-primary">
												<i class="ri-vidicon-line"></i>
											</span>
										</div>
										<div>
											<span class="fw-medium">{{ classroom.name }}</span>
											<small class="text-muted d-block" v-if="classroom.description">{{ truncate(classroom.description, 60) }}</small>
										</div>
									</div>
								</td>
								<td>{{ dateRange(classroom) }}</td>
								<td>{{ classroom.days.length }}</td>
								<td>{{ classroom.stats.chapterCount }}</td>
								<td>{{ classroom.stats.participantCount }}</td>
								<td>
									<span class="badge bg-label-info">{{ providerLabel(classroom.meetingProvider) }}</span>
								</td>
								<td class="text-end" @click.stop>
									<button type="button" class="btn btn-sm btn-icon btn-text-secondary" @click="editClassroom(classroom._id)" data-bs-toggle="tooltip" :data-bs-title="$t('common.actions.edit')">
										<i class="ri-pencil-line"></i>
									</button>
									<button type="button" class="btn btn-sm btn-icon btn-text-danger" @click="deleteClassroom(classroom._id)" data-bs-toggle="tooltip" :data-bs-title="$t('common.actions.delete')">
										<i class="ri-delete-bin-6-line"></i>
									</button>
								</td>
							</tr>
						</tbody>
					</table>
				</div>

				<!-- Empty state -->
				<div class="card-body text-center py-6" v-if="classrooms.length === 0 && !isLoading">
					<i class="ri-vidicon-line ri-48px text-muted mb-3 d-block"></i>
					<p class="text-muted mb-4">{{ $t('virtualClassrooms.list.empty') }}</p>
					<router-link to="/virtual-classrooms/new">
						<button type="button" class="btn btn-primary">
							<i class="ri-add-line me-1"></i>{{ $t('virtualClassrooms.list.createButton') }}
						</button>
					</router-link>
				</div>
			</div>

		</div>
		<div class="content-backdrop fade"></div>
	</div>
</template>

<script>
import HTTP from "@/axios";

export default {
	name: "VirtualClassroomsList",

	data() {
		return {
			classrooms: [],
			isLoading: false,
		};
	},

	methods: {
		openDetail(classroomId) {
			this.$router.push(`/virtual-classrooms/detail/${classroomId}`);
		},

		editClassroom(classroomId) {
			this.$router.push(`/virtual-classrooms/edit/${classroomId}`);
		},

		async fetchClassrooms() {
			this.isLoading = true;
			try {
				const response = await HTTP.get("/virtual-classrooms", { params: { page: 1, limit: 100 } });
				this.classrooms = response.data.classrooms;
			} catch (error) {
				const errorMessage = error?.response?.data?.message || this.$t("errors.generic");
				alert(errorMessage);
				console.error("Erreur API:", error);
			} finally {
				this.isLoading = false;
			}
		},

		async deleteClassroom(classroomId) {
			if (!confirm(this.$t("virtualClassrooms.list.confirmDelete"))) return;

			try {
				await HTTP.delete(`/virtual-classrooms/${classroomId}`);
				this.fetchClassrooms();
			} catch (error) {
				const errorMessage = error?.response?.data?.message || this.$t("errors.generic");
				alert(errorMessage);
				console.error("Erreur API:", error);
			}
		},

		dateRange(classroom) {
			if (!classroom.days || classroom.days.length === 0) return "-";
			const first = this.formatDate(classroom.days[0].date);
			const last = this.formatDate(classroom.days[classroom.days.length - 1].date);
			return first === last ? first : `${first} — ${last}`;
		},

		formatDate(dateStr) {
			if (!dateStr) return "-";
			return dateStr.split("-").reverse().join("/");
		},

		providerLabel(provider) {
			return this.$t(`virtualClassrooms.providers.${provider}`);
		},

		truncate(str, maxLen) {
			if (!str) return "";
			return str.length > maxLen ? str.substring(0, maxLen) + "..." : str;
		},
	},

	async mounted() {
		await this.fetchClassrooms();
	},
};
</script>

<style scoped>
.cursor-pointer {
	cursor: pointer;
}
</style>
